import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export interface LandingPage {
  path: string;
  title: string;
  description: string;
  heading: string;
  highlight: string;
  intro: string;
  sections: { h: string; p?: string[]; list?: string[] }[];
  faq: { q: string; a: string }[];
}

export const TRADEMARK_NOTICE =
  'Etsy, Canva, Amazon KDP, Printify and other names mentioned are trademarks of their respective owners. Image DPI Converter is an independent tool and is not affiliated with or endorsed by them.';

// Use-case guide shown under the converter on landing pages like /etsy-300-dpi
const LandingContent: React.FC<{ page: LandingPage }> = ({ page }) => (
  <section className="mt-10 bg-white rounded-xl border border-gray-200 p-6 md:p-8 text-gray-700">
    <Helmet>
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: page.faq.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
      })}</script>
    </Helmet>
    <div className="space-y-6">
      {page.sections.map(s => (
        <div key={s.h}>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">{s.h}</h2>
          {s.p?.map(t => <p key={t} className="mb-2 leading-relaxed">{t}</p>)}
          {s.list && (
            <ul className="list-disc ml-5 space-y-1">
              {s.list.map(t => <li key={t}>{t}</li>)}
            </ul>
          )}
        </div>
      ))}
      <div>
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Frequently asked questions</h2>
        <div className="space-y-3">
          {page.faq.map(f => (
            <details key={f.q} className="rounded-lg border border-gray-200 p-4">
              <summary className="cursor-pointer font-medium text-gray-900">{f.q}</summary>
              <p className="mt-2 text-gray-600">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
      <p className="text-sm text-gray-500">
        Not sure your image is big enough? Try the free <Link to="/dpi-checker" className="text-blue-700 underline">DPI checker</Link> or <Link to="/print-size-checker" className="text-blue-700 underline">print size checker</Link>.
      </p>
      <p className="text-xs text-gray-400 border-t border-gray-100 pt-4">
        {TRADEMARK_NOTICE}
      </p>
    </div>
  </section>
);

export default LandingContent;
